import React, { useState, useEffect } from 'react';
import { supabase, adminService } from '../services/supabaseClient';
import { eventAdminService } from '../services/eventAdminService';
import { useAuth } from '../contexts/AuthProvider';
import toast, { Toaster } from 'react-hot-toast';
import AdminHeader from './AdminHeader';

const AdminSettings = () => {
    const { user: currentUser } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [newUserEmail, setNewUserEmail] = useState('');
    const [newUserPassword, setNewUserPassword] = useState('');
    const [newUserRole, setNewUserRole] = useState('admin');

    // Password Reset State
    const [resetModalOpen, setResetModalOpen] = useState(false);
    const [targetResetUser, setTargetResetUser] = useState(null);
    const [newResetPassword, setNewResetPassword] = useState('');

    // Change-role State
    const [roleModalOpen, setRoleModalOpen] = useState(false);
    const [roleTargetUser, setRoleTargetUser] = useState(null);
    const [newRoleValue, setNewRoleValue] = useState('admin');
    const [roleSaving, setRoleSaving] = useState(false);

    // Event-assignment State (for event_admin users)
    const [assignModalOpen, setAssignModalOpen] = useState(false);
    const [assignTargetUser, setAssignTargetUser] = useState(null);
    const [allEvents, setAllEvents] = useState([]);
    const [selectedEventIds, setSelectedEventIds] = useState([]);
    const [assignLoading, setAssignLoading] = useState(false);
    const [assignSaving, setAssignSaving] = useState(false);

    // Filters
    const [searchQuery, setSearchQuery] = useState('');
    const [roleFilter, setRoleFilter] = useState('all'); // 'all' | 'admin' | 'super_admin' | 'event_admin'

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            const { data, error } = await supabase
                .from('app_users')
                .select('*')
                .order('created_at', { ascending: false });

            if (error) throw error;
            setUsers(data || []);
        } catch (error) {
            toast.error('Failed to load users');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateUser = async (e) => {
        e.preventDefault();
        try {
            // Call Edge Function
            const { error } = await supabase.functions.invoke('manage-users', {
                body: {
                    action: 'create_user',
                    email: newUserEmail,
                    password: newUserPassword,
                    role: newUserRole
                }
            });

            if (error) throw error;
            // The edge function might return 400 with a clean error message in body
            // check response structure if needed, but supabase client throws on non-200 if configured, 
            // or we check data/error. 
            // supabase-js v2 invoke usually returns { data, error } where error is network/invocation error.
            // If function returns 400, it's often in 'error' or we parse data.

            toast.success('User created successfully');
            setModalOpen(false);
            setNewUserEmail('');
            setNewUserPassword('');
            fetchUsers();
        } catch (error) {
            toast.error(error.message || 'Failed to create user');
            console.error(error);
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        if (!targetResetUser) return;

        try {
            const { error } = await supabase.functions.invoke('manage-users', {
                body: {
                    action: 'reset_password',
                    userId: targetResetUser.id,
                    newPassword: newResetPassword
                }
            });

            if (error) throw error;

            toast.success('Password reset successfully');
            setResetModalOpen(false);
            setNewResetPassword('');
            setTargetResetUser(null);
        } catch (error) {
            toast.error(error.message || 'Failed to reset password');
            console.error(error);
        }
    };

    const openResetModal = (user) => {
        setTargetResetUser(user);
        setResetModalOpen(true);
    };

    // --- Change role ---
    const openRoleModal = (user) => {
        setRoleTargetUser(user);
        setNewRoleValue(user.role);
        setRoleModalOpen(true);
    };

    const handleChangeRole = async (e) => {
        e.preventDefault();
        if (!roleTargetUser) return;
        // Extra guard against self-lockout (button is also hidden for the current user).
        if (currentUser?.id === roleTargetUser.id) {
            toast.error("You can't change your own role");
            return;
        }
        if (newRoleValue === roleTargetUser.role) {
            setRoleModalOpen(false);
            setRoleTargetUser(null);
            return;
        }
        setRoleSaving(true);
        try {
            const { error } = await supabase
                .from('app_users')
                .update({ role: newRoleValue })
                .eq('id', roleTargetUser.id);
            if (error) throw error;

            // If demoting out of event_admin, clear now-meaningless event assignments
            // so they don't silently reappear if the user is later re-promoted.
            if (roleTargetUser.role === 'event_admin' && newRoleValue !== 'event_admin') {
                const { error: delErr } = await supabase
                    .from('event_admin_assignments')
                    .delete()
                    .eq('user_id', roleTargetUser.id);
                if (delErr) console.error('Failed to clear old event assignments:', delErr);
            }

            toast.success('Role updated. The user must sign out and back in for it to take effect.');
            setRoleModalOpen(false);
            setRoleTargetUser(null);
            fetchUsers();
        } catch (err) {
            console.error(err);
            toast.error(err.message || 'Failed to update role');
        } finally {
            setRoleSaving(false);
        }
    };

    // --- Event assignment (event_admin users) ---
    const openAssignModal = async (user) => {
        setAssignTargetUser(user);
        setAssignModalOpen(true);
        setAssignLoading(true);
        try {
            const [{ data: events, error: eventsErr }, assignedIds] = await Promise.all([
                adminService.getAllEvents(),
                eventAdminService.listAssignments(user.id),
            ]);
            if (eventsErr) throw eventsErr;
            setAllEvents(events || []);
            setSelectedEventIds(assignedIds || []);
        } catch (err) {
            console.error(err);
            toast.error('Failed to load event assignments');
        } finally {
            setAssignLoading(false);
        }
    };

    const toggleSelectedEvent = (eventId) => {
        setSelectedEventIds(prev =>
            prev.includes(eventId) ? prev.filter(id => id !== eventId) : [...prev, eventId]
        );
    };

    const handleSaveAssignments = async () => {
        if (!assignTargetUser) return;
        setAssignSaving(true);
        try {
            await eventAdminService.setAssignments(assignTargetUser.id, selectedEventIds);
            toast.success('Event assignments updated');
            setAssignModalOpen(false);
            setAssignTargetUser(null);
        } catch (err) {
            console.error(err);
            toast.error(err.message || 'Failed to update assignments');
        } finally {
            setAssignSaving(false);
        }
    };

    // Badge color per role
    const roleBadgeClass = (role) => {
        if (role === 'super_admin') return 'bg-purple-100 text-purple-800';
        if (role === 'event_admin') return 'bg-emerald-100 text-emerald-800';
        return 'bg-blue-100 text-blue-800';
    };

    if (loading) return <div className="p-8 text-center">Loading settings...</div>;

    // Apply search + role filters client-side
    const trimmedSearch = searchQuery.trim().toLowerCase();
    const filteredUsers = users.filter(u => {
        const matchesSearch = !trimmedSearch || (u.email || '').toLowerCase().includes(trimmedSearch);
        const matchesRole = roleFilter === 'all' || u.role === roleFilter;
        return matchesSearch && matchesRole;
    });

    return (
        <div className="min-h-screen bg-gray-50">
            <Toaster position="top-right" />

            <AdminHeader currentPage="settings" />

            <div className="bg-white border-b shadow-sm">
                <div className="max-w-7xl mx-auto px-6 py-8">
                    <h1 className="text-3xl font-black text-gray-900 tracking-tight">System Settings</h1>
                    <p className="text-gray-500 mt-1">Manage users, permissions and global system configurations.</p>
                </div>
            </div>

            <main className="max-w-7xl mx-auto px-6 py-8">
                <div className="bg-white rounded-xl shadow-lg p-6">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-xl font-bold text-gray-800">User Management</h2>
                        <button
                            onClick={() => setModalOpen(true)}
                            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
                        >
                            + Create User
                        </button>
                    </div>

                    {/* Filters */}
                    <div className="flex flex-col sm:flex-row gap-3 mb-4">
                        <div className="relative flex-1">
                            <input
                                type="text"
                                placeholder="Search by email..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full px-4 py-2 pl-10 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
                            />
                            <svg className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            {searchQuery && (
                                <button
                                    onClick={() => setSearchQuery('')}
                                    className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                                    aria-label="Clear search"
                                >
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            )}
                        </div>
                        <select
                            value={roleFilter}
                            onChange={(e) => setRoleFilter(e.target.value)}
                            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white sm:min-w-[180px]"
                        >
                            <option value="all">All Roles</option>
                            <option value="admin">Admin</option>
                            <option value="super_admin">Super Admin</option>
                            <option value="event_admin">Event Admin</option>
                        </select>
                    </div>

                    {/* Result count hint */}
                    {(trimmedSearch || roleFilter !== 'all') && (
                        <p className="text-xs text-gray-500 mb-3">
                            Showing {filteredUsers.length} of {users.length} users
                        </p>
                    )}

                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead className="bg-gray-100 text-gray-600 uppercase text-sm">
                                <tr>
                                    <th className="px-4 py-3">Email</th>
                                    <th className="px-4 py-3">Role</th>
                                    <th className="px-4 py-3">Created At</th>
                                    <th className="px-4 py-3">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {filteredUsers.length === 0 && users.length > 0 ? (
                                    <tr>
                                        <td colSpan={4} className="px-4 py-12 text-center">
                                            <p className="text-sm text-gray-500">No users match your filters.</p>
                                            <button
                                                onClick={() => { setSearchQuery(''); setRoleFilter('all'); }}
                                                className="mt-2 text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                                            >
                                                Clear filters
                                            </button>
                                        </td>
                                    </tr>
                                ) : filteredUsers.map(u => (
                                    <tr key={u.id} className="hover:bg-gray-50">
                                        <td className="px-4 py-3">{u.email}</td>
                                        <td className="px-4 py-3">
                                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${roleBadgeClass(u.role)}`}>
                                                {u.role}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-gray-500 text-sm">
                                            {new Date(u.created_at).toLocaleDateString()}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-3">
                                                {u.role === 'event_admin' && (
                                                    <button
                                                        onClick={() => openAssignModal(u)}
                                                        className="text-sm text-emerald-600 hover:text-emerald-800 font-medium"
                                                    >
                                                        Manage Events
                                                    </button>
                                                )}
                                                {/* Can't change your own role (avoids self-lockout). */}
                                                {currentUser?.id !== u.id && (
                                                    <button
                                                        onClick={() => openRoleModal(u)}
                                                        className="text-sm text-gray-700 hover:text-gray-900 font-medium"
                                                    >
                                                        Change Role
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => openResetModal(u)}
                                                    className="text-sm text-indigo-600 hover:text-indigo-900 font-medium"
                                                >
                                                    Reset Password
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </main>

            {/* Create User Modal */}
            {modalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg w-full max-w-md p-6">
                        <h3 className="text-lg font-bold mb-4">Create New User</h3>
                        <form onSubmit={handleCreateUser} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Email</label>
                                <input
                                    type="email" required
                                    className="w-full px-3 py-2 border rounded"
                                    value={newUserEmail} onChange={e => setNewUserEmail(e.target.value)}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Password</label>
                                <input
                                    type="password" required
                                    className="w-full px-3 py-2 border rounded"
                                    value={newUserPassword} onChange={e => setNewUserPassword(e.target.value)}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Role</label>
                                <select
                                    className="w-full px-3 py-2 border rounded"
                                    value={newUserRole} onChange={e => setNewUserRole(e.target.value)}
                                >
                                    <option value="admin">Admin</option>
                                    <option value="super_admin">Super Admin</option>
                                    <option value="event_admin">Event Admin</option>
                                </select>
                                {newUserRole === 'event_admin' && (
                                    <p className="text-xs text-gray-500 mt-1">
                                        After creating, use <strong>Manage Events</strong> to assign which events this user can access.
                                    </p>
                                )}
                            </div>
                            <div className="flex justify-end gap-3 mt-6">
                                <button type="button" onClick={() => setModalOpen(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded">Cancel</button>
                                <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700">Create</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Change Role Modal */}
            {roleModalOpen && roleTargetUser && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg w-full max-w-md p-6">
                        <h3 className="text-lg font-bold mb-2">Change Role</h3>
                        <p className="text-sm text-gray-600 mb-4">
                            Update the role for <strong>{roleTargetUser.email}</strong>.
                        </p>
                        <form onSubmit={handleChangeRole} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Role</label>
                                <select
                                    className="w-full px-3 py-2 border rounded"
                                    value={newRoleValue}
                                    onChange={e => setNewRoleValue(e.target.value)}
                                >
                                    <option value="admin">Admin</option>
                                    <option value="super_admin">Super Admin</option>
                                    <option value="event_admin">Event Admin</option>
                                </select>
                            </div>

                            {newRoleValue === 'event_admin' && roleTargetUser.role !== 'event_admin' && (
                                <p className="text-xs text-gray-500">
                                    After saving, use <strong>Manage Events</strong> to assign which events this user can access.
                                </p>
                            )}
                            {roleTargetUser.role === 'event_admin' && newRoleValue !== 'event_admin' && (
                                <p className="text-xs text-amber-600">
                                    This user's existing event assignments will be removed.
                                </p>
                            )}

                            <p className="text-xs text-gray-400">
                                The user must sign out and back in for the new role to take effect.
                            </p>

                            <div className="flex justify-end gap-3 mt-6">
                                <button
                                    type="button"
                                    onClick={() => { setRoleModalOpen(false); setRoleTargetUser(null); }}
                                    className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
                                    disabled={roleSaving}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={roleSaving}
                                    className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
                                >
                                    {roleSaving ? 'Saving...' : 'Save Role'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Manage Event Assignments Modal */}
            {assignModalOpen && assignTargetUser && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg w-full max-w-lg p-6 max-h-[90vh] flex flex-col">
                        <h3 className="text-lg font-bold mb-1">Manage Events</h3>
                        <p className="text-sm text-gray-600 mb-4">
                            Select which events <strong>{assignTargetUser.email}</strong> can access and manage.
                        </p>

                        {assignLoading ? (
                            <div className="py-12 text-center text-gray-500">Loading events...</div>
                        ) : allEvents.length === 0 ? (
                            <div className="py-12 text-center text-gray-500">No events exist yet.</div>
                        ) : (
                            <div className="flex-1 overflow-y-auto border rounded-lg divide-y">
                                {allEvents.map(ev => (
                                    <label
                                        key={ev.id}
                                        className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedEventIds.includes(ev.id)}
                                            onChange={() => toggleSelectedEvent(ev.id)}
                                            className="h-4 w-4 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500"
                                        />
                                        <span className="flex-1">
                                            <span className="block text-sm font-medium text-gray-800">{ev.name}</span>
                                            <span className="block text-xs text-gray-400 font-mono">{ev.code}{ev.is_archived ? ' · archived' : ''}</span>
                                        </span>
                                    </label>
                                ))}
                            </div>
                        )}

                        <div className="flex justify-between items-center gap-3 mt-6">
                            <span className="text-xs text-gray-500">{selectedEventIds.length} selected</span>
                            <div className="flex gap-3">
                                <button
                                    type="button"
                                    onClick={() => { setAssignModalOpen(false); setAssignTargetUser(null); }}
                                    className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
                                    disabled={assignSaving}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleSaveAssignments}
                                    disabled={assignSaving || assignLoading}
                                    className="px-4 py-2 bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50"
                                >
                                    {assignSaving ? 'Saving...' : 'Save Assignments'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Reset Password Modal */}
            {resetModalOpen && targetResetUser && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg w-full max-w-md p-6">
                        <h3 className="text-lg font-bold mb-2">Reset Password</h3>
                        <p className="text-sm text-gray-600 mb-4">Set a new password for <strong>{targetResetUser.email}</strong>.</p>
                        <form onSubmit={handleResetPassword} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">New Password</label>
                                <input
                                    type="password" required
                                    className="w-full px-3 py-2 border rounded"
                                    value={newResetPassword} onChange={e => setNewResetPassword(e.target.value)}
                                    placeholder="Enter new password"
                                />
                            </div>
                            <div className="flex justify-end gap-3 mt-6">
                                <button type="button" onClick={() => setResetModalOpen(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded">Cancel</button>
                                <button type="submit" className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700">Reset Password</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminSettings;
